<script lang="ts" runes>
    import { onMount } from "svelte";
    import { Section } from "@/components";
    import { translate } from "@/utils/format";
    import appStore from "@/utils/store";
    import { getUserRequirements } from "@/services/user";
    import {
        cancelAllNotifications,
        checkAndRequestPermissions,
        scheduleTrackingReminders,
    } from "@/services/localNotifications";
    import { showDebugOption } from "@/utils/logs";
    import { trackEvent } from "@/services/firebase";
    // Estado local
    let settings = $state({
        notifications: true,
        healthIntegration: true,
        estimateCalories: false,
        reminders: true,
        darkMode: false,
        savePhotos: true,
        aIModel: "gemini", // "openai" or "gemini"
        useLocation: true, // Nuevo campo
    });

    createSettings();

    // Cargar config si existe
    onMount(() => {
        const saved = appStore.get("settingsCaloo");
        if (saved) {
            settings = { ...settings, ...saved };
        }
        trackEvent("configuration_loaded");
    });
    // Guardar cambios
    function createSettings() {
        if (!appStore.get("settingsCaloo")) {
            console.log("Creating default settings");
            appStore.set("settingsCaloo", settings);
        }
    }
    // Guardar cambios
    function saveSettings() {
        // console.log("Guardando configuración:", settings);
        appStore.set("settingsCaloo", settings);
    }

    // Cambiar un campo y guardar
    function toggleSetting(key: keyof typeof settings) {
        settings[key] = !settings[key];
        settings = { ...settings }; // Forzar reactividad
        saveSettings();
        switch (key) {
            case "darkMode":
                if (settings.darkMode) {
                    document.body.classList.add("dark");
                } else {
                    document.body.classList.remove("dark");
                }
                break;

            case "notifications":
                if (settings.notifications) {
                    console.log("🔔 Solicitando permisos de notificaciones...");
                    checkAndRequestPermissions().then((granted) => {
                        if (granted) {
                            console.log("✅ Permiso concedido.");
                            // Si quieres, programa un recordatorio diario por defecto
                            scheduleTrackingReminders();
                        } else {
                            console.warn("⚠️ Permiso denegado por el usuario.");
                            // Revertir el cambio si no hay permiso
                            settings.notifications = false;
                            settings = { ...settings };
                        }
                        saveSettings();
                    });
                    return; // Espera a la promesa
                } else {
                    console.log("🛑 Cancelando notificaciones...");
                    cancelAllNotifications();
                }
                break;
            case "useLocation":
                if (settings.useLocation) {
                    import("@/services/location").then(async (module) => {
                        const granted =
                            await module.checkAndRequestLocationPermission();
                        if (!granted) {
                            console.warn(
                                "❌ Permiso de localización no concedido.",
                            );
                            settings.useLocation = false;
                            settings = { ...settings };
                            saveSettings();
                        } else {
                            console.log(
                                "📍 Permiso de localización concedido.",
                            );
                        }
                    });
                }
                break;
            case "savePhotos":
                if (settings.savePhotos) {
                    console.log("💾 Las fotos se guardarán en la galería.");
                }
                break;

            case "healthIntegration":
                if (settings.healthIntegration) {
                    if (settings.estimateCalories) {
                        getUserRequirements(true);
                    }
                    console.log("🩺 Integración de salud activada.");
                } else {
                    if (settings.estimateCalories) {
                        getUserRequirements(true);
                    }
                    console.log("🩺 Integración de salud desactivada.");
                }
                break;

            case "estimateCalories":
                getUserRequirements(true);
                console.log(
                    `🔥 Estimaremos calorías automáticamente: ${settings.estimateCalories}. Recalculamos requisitos!`,
                );
                break;

            case "reminders":
                console.log(`⏰ Recordatorios diarios: ${settings.reminders}`);
                break;

            default:
                console.log(`⚙️ Ajuste modificado: ${key}`);
        }
    }
</script>

{#snippet section()}
    <section class="configurationPage">
        <div class="configurationPage__group">
            <h2 class="configurationPage__groupTitle">
                {translate("notifications", "Notificaciones")}
            </h2>
            <div class="configurationPage__item">
                <span
                    >{translate(
                        "notificationsGeneral",
                        "Activar notificaciones",
                    )}</span
                >
                <label class="switch">
                    <input
                        type="checkbox"
                        checked={settings.notifications}
                        onchange={() => toggleSetting("notifications")}
                    />
                    <span class="slider"></span>
                </label>
            </div>
            {#if settings.notifications}
                <div class="configurationPage__item">
                    <span
                        >{translate("reminders", "Recordatorios diarios")}</span
                    >
                    <label class="switch">
                        <input
                            type="checkbox"
                            checked={settings.reminders}
                            onchange={() => toggleSetting("reminders")}
                        />
                        <span class="slider"></span>
                    </label>
                </div>
            {/if}
            {#if settings.notifications}
                <div class="configurationPage__item">
                    <span
                        >{translate(
                            "notificationsDescription",
                            "Recibirás notificaciones para recordatorios y consejos.",
                        )}</span
                    >
                </div>
            {/if}
        </div>

        <div class="configurationPage__group">
            <h2 class="configurationPage__groupTitle">
                {translate("health", "Salud y actividad")}
            </h2>
            <div class="configurationPage__item">
                <span
                    >{translate(
                        "healthIntegration",
                        "Integrar con Health",
                    )}</span
                >
                <label class="switch">
                    <input
                        type="checkbox"
                        checked={settings.healthIntegration}
                        onchange={() => toggleSetting("healthIntegration")}
                    />
                    <span class="slider"></span>
                </label>
            </div>
            {#if !settings.healthIntegration}
                <div class="configurationPage__item">
                    <span
                        >{translate(
                            "estimateCalories",
                            "Estimar calorías automáticamente",
                        )}</span
                    >
                    <label class="switch">
                        <input
                            type="checkbox"
                            checked={settings.estimateCalories}
                            onchange={() => toggleSetting("estimateCalories")}
                        />
                        <span class="slider"></span>
                    </label>
                </div>
            {/if}
        </div>

        <div class="configurationPage__group">
            <h2 class="configurationPage__groupTitle">
                {translate("preferences", "Preferencias")}
            </h2>
            <div class="configurationPage__item">
                <span
                    >{translate(
                        "USE_LOCATION",
                        "Usar localización para mejorar resultados",
                    )}</span
                >
                <label class="switch">
                    <input
                        type="checkbox"
                        checked={settings.useLocation}
                        onchange={() => toggleSetting("useLocation")}
                    />
                    <span class="slider"></span>
                </label>
            </div>
            <!-- <div class="configurationPage__item">
                <span>{translate("darkMode", "Modo oscuro")}</span>
                <label class="switch">
                    <input
                        type="checkbox"
                        checked={settings.darkMode}
                        onchange={() => toggleSetting("darkMode")}
                    />
                    <span class="slider"></span>
                </label>
            </div> -->
            {#if showDebugOption}
                <div class="configurationPage__item">
                    <span>
                        {translate("aIModel", "AI model for image analysis")}
                    </span>
                    <select
                        class="configurationPage__select"
                        value={settings.aIModel}
                        onchange={(value) => {
                            settings.aIModel = value?.target?.value;
                            saveSettings();
                        }}
                    >
                        <option value="openai">AI model #1 (O)</option>
                        <option value="gemini">AI model #2 (G)</option>
                    </select>
                </div>
            {/if}
            <div class="configurationPage__item">
                <span
                    >{translate("savePhotos", "Guardar fotos en galería")}</span
                >
                <label class="switch">
                    <input
                        type="checkbox"
                        checked={settings.savePhotos}
                        onchange={() => toggleSetting("savePhotos")}
                    />
                    <span class="slider"></span>
                </label>
            </div>
        </div>
    </section>
{/snippet}

<Section
    headerText={translate("configuration", "Configuración")}
    actionBack="settings"
    returnBtn={true}
    className="configurationPage"
    {section}
/>

<style lang="scss">
    @use "./styles/index.scss";
</style>
